import { useEffect, useState, type FormEvent } from 'react';
import { ApiError, type AccountSession, type AmoyeApi } from '../lib/api';
import type { PersonaId } from '../types/persona';
import { Brand } from './Brand';
import { Button } from './Button';
import { ThemeToggle } from './ThemeToggle';
import { presentationPath, useIOSPresentation } from '../lib/web-presentation';
import { productPath } from '../lib/product-routes';
import { startAuthentication } from '@simplewebauthn/browser';
import { PasswordField } from './PasswordField';

interface LoginScreenProps {
  api: AmoyeApi;
  initialProduct?: PersonaId;
  notice?: string;
  stayInAccountPortal?: boolean;
  createAccountReturnTo?: string;
  onAuthenticated: (session: AccountSession) => void;
}

const loginProducts: readonly { id: PersonaId; label: string; surface: string }[] = [
  { id: 'practitioner', label: 'Tax practice', surface: 'Tax practice' },
  { id: 'business', label: 'Business tax', surface: 'Business tax' },
  { id: 'individual', label: 'Personal tax', surface: 'Personal tax' },
];

function signInMessage(error: unknown): string {
  if (!(error instanceof ApiError)) return 'Sign-in is temporarily unavailable. Please try again.';
  if (error.status === 401) return 'Email or password is incorrect.';
  if (error.status === 403 && error.code === 'product_access_denied') {
    return 'This account does not have access to this Amoye product.';
  }
  if (error.status === 429) return 'Too many sign-in attempts. Please wait before trying again.';
  if (error.status === 503) return 'Sign-in is temporarily unavailable.';
  return 'Sign-in could not be completed. Please try again.';
}

export function LoginScreen({ api, initialProduct = 'practitioner', notice, stayInAccountPortal=false, createAccountReturnTo, onAuthenticated }: LoginScreenProps) {
  const ios = useIOSPresentation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [passkeyFirst,setPasskeyFirst]=useState(false);
  const [mfa,setMfa]=useState<{id:string;token:string;passkey:boolean;totp:boolean;recovery:boolean}|null>(null),[code,setCode]=useState('');
  const selectedProduct = loginProducts.find(({ id }) => id === initialProduct) ?? loginProducts[0]!;
  useEffect(()=>{
    let current=true;
    setPasskeyFirst(false);
    if(!ios&&typeof api.accountCapabilities==='function')void api.accountCapabilities()
      .then(c=>{if(current)setPasskeyFirst(c.secure_accounts&&c.passkey_first===true);}).catch(()=>{});
    return ()=>{current=false;};
  },[api,ios]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPending(true);
    setError('');
    try {
      const session = await api.createSession(email, password, selectedProduct.id);
      if (session.mfa_required && session.challenge_id && session.challenge_token) {
        setMfa({id:session.challenge_id,token:session.challenge_token,passkey:session.passkey_available!==false,
          totp:session.totp_available!==false,recovery:session.recovery_code_available!==false});setPassword('');
      } else finish(session);
    } catch (reason) {
      setError(signInMessage(reason));
    } finally {
      setPending(false);
    }
  }
  function finish(session:AccountSession) {
    if(!stayInAccountPortal&&(session.verification_required||session.mfa_enrollment_required||session.subscription_required)){window.location.assign(`/account/settings?product=${selectedProduct.id}`);return;}
    onAuthenticated(session);
  }
  async function finishMfa(event:FormEvent) {
    event.preventDefault();if(!mfa)return;setPending(true);setError('');
    try {finish(await api.accountRequest<AccountSession>('/auth/accounts/mfa/login',selectedProduct.id,{challenge_id:mfa.id,challenge_token:mfa.token,code}));}
    catch(reason){setError(signInMessage(reason));}finally{setPending(false);}
  }
  async function usePasskey() {
    if(!mfa)return;setPending(true);setError('');
    try {
      const challenge=await api.accountRequest<{options:Parameters<typeof startAuthentication>[0]['optionsJSON'];challenge_id:string;challenge_token:string}>(
        '/auth/accounts/passkeys/authenticate/options',selectedProduct.id,{login_id:mfa.id,login_token:mfa.token});
      const response=await startAuthentication({optionsJSON:challenge.options});
      finish(await api.accountRequest<AccountSession>('/auth/accounts/passkeys/authenticate/verify',selectedProduct.id,{challenge_id:challenge.challenge_id,challenge_token:challenge.challenge_token,response}));
    }catch{setError('Passkey verification did not complete. Try again or use another enrolled method.');}finally{setPending(false);}
  }
  async function signInWithPasskey() {
    if(pending)return;
    setPending(true);setError('');
    try {
      const challenge=await api.accountRequest<{options:Parameters<typeof startAuthentication>[0]['optionsJSON'];challenge_id:string;challenge_token:string}>(
        '/auth/accounts/passkeys/login/options',selectedProduct.id,{product:selectedProduct.id});
      const response=await startAuthentication({optionsJSON:challenge.options});
      finish(await api.accountRequest<AccountSession>('/auth/accounts/passkeys/login/verify',selectedProduct.id,
        {product:selectedProduct.id,challenge_id:challenge.challenge_id,challenge_token:challenge.challenge_token,response}));
    }catch(reason){setError(reason instanceof ApiError&&reason.status===429?signInMessage(reason):
      `Passkey sign-in did not complete. Choose a passkey for ${selectedProduct.label}, try again, or use your email and password.`);}
    finally{setPending(false);}
  }

  return (
    <main className="login-screen">
      <div className="login-screen__chrome">
        <Brand surface={selectedProduct.surface} />
        <ThemeToggle />
      </div>
      <section className="login-card" aria-labelledby="login-title">
        {ios && <p className="eyebrow">ACCOUNT ACCESS</p>}
        <h1 id="login-title">{ios ? 'Sign in to Amoye' : `Sign in to ${selectedProduct.label}`}</h1>
        <p>{ios ? 'Use your account to continue.' : passkeyFirst?'Use your passkey, or sign in with your email and password.':'Use your email and password to continue.'}</p>
        {ios && <nav aria-label="Choose product" className="ios-product-picker">{loginProducts.map((product) =>
          <a aria-current={product.id === initialProduct ? 'page' : undefined} href={productPath(product.id)} key={product.id}>{product.label}</a>)}</nav>}
        {notice && <p className="login-card__notice" role="status">{notice}</p>}
        {passkeyFirst&&!mfa&&<Button disabled={pending} type="button" onClick={()=>void signInWithPasskey()}>Sign in with a passkey</Button>}
        {mfa?<form onSubmit={finishMfa} aria-busy={pending}>
          <p>Confirm this {selectedProduct.label} sign-in with {mfa.passkey?'your passkey':mfa.totp?'your authenticator app':'a one-time recovery code'}.</p>
          {mfa.passkey&&<Button disabled={pending} type="button" onClick={()=>void usePasskey()}>Use passkey</Button>}
          {(mfa.totp||mfa.recovery)&&<><label className="field-label" htmlFor="mfa-code">{mfa.totp&&mfa.recovery?'Authenticator or recovery code':mfa.totp?'Authenticator code':'Recovery code'}</label>
          <input id="mfa-code" autoComplete="one-time-code" value={code} onChange={e=>setCode(e.target.value.trim())} required/>
          <Button disabled={pending} type="submit">Verify and sign in</Button></>}
          {error&&<p role="alert">{error}</p>}
          {!mfa.passkey&&!mfa.totp&&!mfa.recovery&&<p role="alert">No enrolled sign-in method is available. Start again or contact support.</p>}
          <button type="button" onClick={()=>setMfa(null)}>Start again</button>
        </form>:<form onSubmit={submit} aria-busy={pending}>
          <label className="field-label" htmlFor="account-email">Email</label>
          <input autoComplete="username" autoFocus={!ios} id="account-email" inputMode="email" name="email" required type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
          <PasswordField autoComplete="current-password" id="account-password" label="Password" labelClassName="field-label"
            name="password" required value={password} onChange={(event) => setPassword(event.target.value)} />
          {error && <p className="login-card__error" role="alert">{error}</p>}
          <Button disabled={pending} tone={passkeyFirst?'secondary':'primary'} type="submit">{pending ? 'Signing in…' : 'Sign in'}</Button>
          <span className="am-visually-hidden" aria-live="polite">{pending ? 'Signing in' : ''}</span>
        </form>}
        <a className="login-card__create" href={`${productPath(selectedProduct.id)}/get-started${createAccountReturnTo?`?return_to=${encodeURIComponent(createAccountReturnTo)}`:''}`}>Create a new account</a>
        <a className="login-card__create" href={presentationPath('/account-recovery')}>Forgot your sign-in email?</a>
        <a className="login-card__create" href={`/account/recover?product=${selectedProduct.id}`}>Reset your password</a>
      </section>
    </main>
  );
}
